import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Download, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Badge } from "../../../../ui/badge";
import { Button } from "../../../../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../../../ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../ui/table";
import { ScenarioHeader } from "../../../../components/portfolio-risk-concentration/ScenarioHeader";
import { ScenarioFacilityCards } from "../../../../components/portfolio-risk-concentration/ScenarioFacilityCards";
import { ScenarioSpinner } from "../../../../components/portfolio-risk-concentration/ScenarioSpinner";
import { ScenarioAsOfPicker, asOfNote } from "../../../../components/portfolio-risk-concentration/ScenarioAsOfPicker";
import { thr } from "../../../../components/portfolio-risk-concentration/format";
import { TotalCards } from "../../../../components/portfolio-risk-concentration/PortfolioTotalCards";
import {
  BOOK_LABEL,
  FILTER_TRIGGER,
  FilterField,
  FilterSelect,
  PRODUCT_FILTERS,
  useFacilityOptions,
  useInvestorFilterOptions,
  type Investor,
  type ProductFilter,
} from "../../../../components/portfolio-risk-concentration/PrcFilterBar";
import { cn } from "../../../../utils/common/cn";
import { downloadCsv } from "../../../../utils/portfolio-risk-concentration/csv";
import {
  UI_DEFAULT_VALUE_BASIS,
  type ValueBasis,
} from "../../../../utils/portfolio-risk-concentration/api/concentration";
import {
  apiDetail,
  downloadScenarioSnapshot,
  scenarioSignature,
  useScenarioMonitorAnswer,
  type ScenarioLimitRow,
  type ScenarioSnapshotKind,
} from "../../../../utils/portfolio-risk-concentration/api/scenario";
import { BASELINE_FACILITIES, useFacilities } from "../../../../utils/portfolio-risk-concentration/api/facilities";
import type { ScenarioDraft } from "../ScenarioSimulatorPage";
import { scenarioBatchInput } from "./scenarioInput";

// Scenario shows the facilities' limits (CAP 1, CAP 2, ABS SCENARIO, ...) in one table. The internal limits are not a facility and are not
// part of it: a scenario that moves contracts between facilities does not change them (they measure the whole book).
// The facilities come from Manage Capital (GET /facilities), so one added there is a choice here without a code change.
type ScenarioFacilityFilter = string;

// All three bases are real here (sql/48_'s scenario-scoped PV/FMV functions), same as real
// Monitor. FMV renders blank for every row today -- business_performance_daily.fmv_final is
// declared but not yet derived (sql/40_'s header) -- which is UNMEASURED, not a wiring gap.
const MEASURED_IN_OPTIONS: { value: ValueBasis; label: string }[] = [
  { value: "net_investment", label: "Book Value" },
  { value: "present_value", label: "Present Value ( DCB)" },
  { value: "fair_market_value", label: "Fair Market Value (FMV)" },
];

// Loans / Leases is not a filter on these rows: it is a different calculation (each limit's Loans or Leases contribution, measured against
// the facility), which the server does when asked for a product -- see the effect below.
function matchesScenarioFilters(r: ScenarioLimitRow, investor: Investor, facility: ScenarioFacilityFilter): boolean {
  if (investor !== "ALL" && r.investor !== investor) return false;
  if (facility !== "ALL" && r.facilityCode !== facility) return false;
  return true;
}

/** 3 · Scenario Monitor — real vs scenario, every affected limit genuinely recomputed. */
export default function ScenarioMonitorStep({
  draft,
  patch,
  base,
}: {
  draft: ScenarioDraft;
  patch: (p: Partial<ScenarioDraft>) => void;
  base: string;
}) {
  const navigate = useNavigate();
  const { investorsWithAll } = useInvestorFilterOptions();
  const facilitiesQ = useFacilities();

  // The filters start where they were left (kept on the draft), so leaving this step and coming back shows the same choices.
  const view = draft.monitorView;
  const [investor, setInvestor] = useState<Investor>((view?.investor as Investor | undefined) ?? "ALL");
  const [facility, setFacility] = useState<ScenarioFacilityFilter>(view?.facility ?? "ALL");

  // Only the facilities the chosen investor has (Manage Capital: its Debt capital sources; CAP 1 and CAP 2 are the external funder's), not
  // every facility there is. "Internal" is not one of them (it is the label of the whole-book internal limits) and is not offered here.
  const facilityFilters: { value: ScenarioFacilityFilter; label: string }[] = useFacilityOptions(investor);
  // A facility picked under another investor is not one of this investor's: back to All.
  useEffect(() => {
    if (!facilitiesQ.data || facility === "ALL") return;
    if (!facilityFilters.some((o) => o.value === facility)) setFacility("ALL");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investor, facilitiesQ.data]);
  const [product, setProduct] = useState<ProductFilter>((view?.product as ProductFilter | undefined) ?? "ALL");
  const [measuredIn, setMeasuredIn] = useState<ValueBasis>(view?.measuredIn ?? UI_DEFAULT_VALUE_BASIS);
  useEffect(() => {
    patch({ monitorView: { investor, facility, product, measuredIn } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investor, facility, product, measuredIn]);

  useEffect(() => {
    if (!draft.waterfall) navigate(`${base}/apply`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The answer for this filter combination. Loans / Leases and Measured in make the server recompute, so each combination is asked once
  // and kept (useScenarioMonitorAnswer): switching back to one already looked at, or coming back to this step, shows it with no request;
  // adding or changing a condition or destination on the Scenario step drops them all. Investor and Facility only narrow the rows. The
  // effective date is part of what is asked too: each date's answers are kept apart, and a new date is computed on that day's snapshot.
  const answer = useScenarioMonitorAnswer(
    scenarioSignature(draft),
    draft.asOfDate,
    product === "ALL" ? undefined : product,
    measuredIn,
    draft.waterfall
      ? () => ({
          ...scenarioBatchInput(draft, facilitiesQ.data ?? BASELINE_FACILITIES),
          product: product === "ALL" ? undefined : product,
          valueBasis: measuredIn,
        })
      : null,
  );
  const result = answer.data ?? null;

  // A request is running (a combination not asked before): show a loader and lock every filter until it answers, so a second change
  // cannot start while the first is still running. Investor and Facility only narrow the rows already here, so they answer at once, but
  // they are locked with the rest.
  const loading = answer.isFetching;
  // The table is the facilities' limits only (CAP 1, CAP 2, ABS SCENARIO, ...): the internal limits are not a facility and are not listed.
  const allGreen = (
    result?.byFacility
      ? Object.values(result.byFacility).flatMap((f) => f.rows)
      : [...(result?.rows.cap1 ?? []), ...(result?.rows.cap2 ?? [])]
  ).every((r) => !r.measured || r.status !== "RED");

  // Every facility's rows (by code, when the server sends them).
  const allRows = useMemo(
    () =>
      (result?.byFacility
        ? Object.values(result.byFacility).flatMap((f) => f.rows)
        : [...(result?.rows.cap1 ?? []), ...(result?.rows.cap2 ?? [])]
      ).filter((r) => r.measured),
    [result],
  );

  // The real (no scenario assignments applied) reading of the same limits, for the "Current"
  // column -- already returned by /scenario/monitor as `realRows`, just never rendered before.
  const realByLimitId = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const r of result?.byFacility
      ? Object.values(result.byFacility).flatMap((f) => f.realRows)
      : [...(result?.realRows.cap1 ?? []), ...(result?.realRows.cap2 ?? [])]) {
      m.set(r.limitId, r.measured ? r.currentDollarPct : null);
    }
    return m;
  }, [result]);

  const filteredRows = useMemo(
    () => allRows.filter((r) => matchesScenarioFilters(r, investor, facility)),
    [allRows, investor, facility],
  );

  // Total value and contract count of the book the Investor and Facility filters name: the scenario's own facility tiles (already on the
  // chosen Measured in basis and Loans / Leases view), added over the facilities that belong to the investor, so "now" is the book today
  // and the figure beside it what it becomes once the assignments are applied. A server that does not send contract counts yet leaves them out.
  const totals = useMemo(() => {
    const owner = new Map((facilitiesQ.data ?? []).map((f) => [f.code, (f.investorCode ?? "").toUpperCase()]));
    const tiles = (result?.tiles ?? []).filter(
      (t) => (investor === "ALL" || owner.get(t.label) === investor.toUpperCase()) && (facility === "ALL" || t.label === facility),
    );
    const sum = (pick: (t: (typeof tiles)[number]) => number | undefined) =>
      tiles.some((t) => pick(t) === undefined) ? null : tiles.reduce((acc, t) => acc + (pick(t) ?? 0), 0);
    // fair market value is not populated yet: its tiles read 0, which is "not measured", not a book of nothing
    const unmeasured = measuredIn === "fair_market_value";
    return {
      now: unmeasured ? null : sum((t) => t.realDollars),
      scenario: unmeasured ? null : sum((t) => t.scenarioDollars),
      nowCount: sum((t) => t.realContracts),
      scenarioCount: sum((t) => t.scenarioContracts),
    };
  }, [result, facilitiesQ.data, investor, facility, measuredIn]);

  // The two contract tables behind these limits (the Snapshot page's PRC Contract Detail and PRC Ineligible Contracts), for this scenario and
  // effective date: the whole table, streamed by the server and saved as a CSV.
  const [downloadingContracts, setDownloadingContracts] = useState<ScenarioSnapshotKind | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  async function downloadContracts(kind: ScenarioSnapshotKind) {
    setDownloadingContracts(kind);
    setDownloadError(null);
    try {
      await downloadScenarioSnapshot(kind, scenarioBatchInput(draft, facilitiesQ.data ?? BASELINE_FACILITIES));
    } catch (err) {
      setDownloadError(apiDetail(err, "Could not download the file."));
    } finally {
      setDownloadingContracts(null);
    }
  }

  // The table as it stands (the rows the filters leave, the same eight columns), as a CSV. The figures go out as plain numbers (percentages
  // in points of the book, 3.5 for 3.50%; nothing for a limit that is not measured), with no % sign, so a spreadsheet can sum and sort them.
  function downloadMonitorCsv() {
    downloadCsv(
      `scenario-monitor-${result?.asOfDate ?? "export"}.csv`,
      ["Limit", "Facility", "Threshold", "Category", "Current", "Scenario", "Status", "Direction"],
      filteredRows.map((r) => [
        r.limitDesc,
        BOOK_LABEL[r.facilityCode] ?? r.facilityCode,
        r.thresholdPct,
        r.categoryGroup,
        realByLimitId.get(r.limitId),
        r.measured ? r.currentDollarPct : null,
        r.status,
        r.direction,
      ]),
    );
  }

  return (
    <div>
      <ScenarioHeader
        title="Scenario Monitor"
        subtitle={`${draft.approvedScenarioLabel ? `Approved scenario ${draft.approvedScenarioLabel} · ` : ""}Existing portfolio plus this session's assignments — nothing here is real yet.`}
        notReal
      />

      {/* Same Back/Next pair as the bottom of the page -- repeated here so a long results
          table doesn't make stepping forward or back mean scrolling all the way down first. */}
      <div className="mt-3 flex justify-between">
        <Button variant="outline" onClick={() => navigate(`${base}/apply`)}>
          ← Scenario
        </Button>
        <Button disabled={!result} onClick={() => navigate(`${base}/snapshot`)}>
          View Scenario Snapshot →
        </Button>
      </div>

      {!result && answer.isFetching ? (
        // Nothing to show yet (the first load of this scenario): a later filter change keeps the previous answer on screen, dimmed, under
        // the loader, so this only ever covers the very first request.
        <ScenarioSpinner label="Computing scenario…" />
      ) : !result && answer.isError ? (
        <>
          {/* The date can be what failed: leave it changeable here, not only on the filter row of a result that is not there. */}
          <div className="mt-3 flex justify-end">
            <ScenarioAsOfPicker value={draft.asOfDate} onChange={(asOfDate) => patch({ asOfDate })} />
          </div>
          <p role="alert" className="mt-4 text-sm text-destructive">
            Could not compute this scenario as of {draft.asOfDate}. Pick another date, or go back to the Scenario step and try again.
          </p>
        </>
      ) : result ? (
        <>
          {/* While the figures are being recomputed the cards are the previous ones: dim them, and leave the banner out (its
              colour comes from rows that are cleared at that moment). */}
          <div className={cn(loading && "pointer-events-none opacity-50")}>
            <ScenarioFacilityCards
              tiles={result.codeTiles ?? []}
              basisLabel={MEASURED_IN_OPTIONS.find((o) => o.value === measuredIn)?.label}
            />
          </div>

          {!loading && (
            <div
              className={cn(
                "mt-3 rounded-md border px-3 py-2 text-xs",
                allGreen
                  ? "border-[color:var(--approve)]/40 bg-[color:var(--approve)]/10 text-[color:var(--approve)]"
                  : "border-destructive/40 bg-destructive/10 text-destructive",
              )}
            >
              {allGreen
                ? `All limits within agreed thresholds under this scenario, as of ${result.asOfDate}.`
                : `One or more limits breach their threshold under this scenario, as of ${result.asOfDate}.`}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <FilterField label="Investor">
              <Select value={investor} onValueChange={(v) => setInvestor(v as Investor)} disabled={loading}>
                <SelectTrigger className={FILTER_TRIGGER} aria-label="Investor">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {investorsWithAll.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
            <FilterSelect<ScenarioFacilityFilter>
              label="Facility"
              value={facility}
              options={facilityFilters}
              onChange={setFacility}
              disabled={loading}
              wide
            />
            <FilterSelect<ProductFilter>
              label="Loans / Leases"
              value={product}
              options={PRODUCT_FILTERS}
              onChange={setProduct}
              disabled={loading}
            />
            <FilterField label="Measured in">
              <Select value={measuredIn} onValueChange={(v) => setMeasuredIn(v as ValueBasis)} disabled={loading}>
                <SelectTrigger className={FILTER_TRIGGER} aria-label="Measured in">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEASURED_IN_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
            {/* Download, then the effective date (same control as on Monitor and Capacity), at the end of the filter row; locked with the
                other filters while a request runs. */}
            <div className="ml-auto flex items-end gap-3">
              <TotalCards
                value={totals.scenario}
                count={totals.scenarioCount}
                currentValue={totals.now}
                currentCount={totals.nowCount}
                basisLabel={MEASURED_IN_OPTIONS.find((o) => o.value === measuredIn)?.label}
                dimmed={loading}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9" disabled={loading} aria-label="Download CSV">
                    {downloadingContracts ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    {downloadingContracts ? "Preparing download…" : "Download CSV"}
                    <ChevronDown className="ml-1.5 h-3.5 w-3.5 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled={filteredRows.length === 0} onSelect={downloadMonitorCsv}>
                    Risk limits
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={downloadingContracts !== null} onSelect={() => void downloadContracts("contracts")}>
                    PRC contracts
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={downloadingContracts !== null} onSelect={() => void downloadContracts("ineligible")}>
                    PRC ineligible contracts
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <ScenarioAsOfPicker value={draft.asOfDate} onChange={(asOfDate) => patch({ asOfDate })} disabled={loading} />
            </div>
          </div>
          {downloadError && <p className="mt-1 text-[11px] text-destructive">{downloadError}</p>}
          {!loading && asOfNote(draft.asOfDate, result.asOfDate) && (
            <p className="mt-1 text-[11px] text-muted-foreground">{asOfNote(draft.asOfDate, result.asOfDate)}</p>
          )}
          {measuredIn === "fair_market_value" && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Fair market value isn't populated by the pipeline yet, so every figure below
              renders blank until it is.
            </p>
          )}

          {loading ? (
          <ScenarioSpinner label="Recomputing scenario…" />
          ) : (
          <div className="mt-2 overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Limit</TableHead>
                  <TableHead>Facility</TableHead>
                  <TableHead className="text-right">Threshold</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Current</TableHead>
                  <TableHead className="text-right">Scenario</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Direction</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                      Nothing matches these filters.
                    </TableCell>
                  </TableRow>
                )}
                {filteredRows.map((r) => {
                  const realPct = realByLimitId.get(r.limitId);
                  return (
                  <TableRow key={r.limitId}>
                    <TableCell>{r.limitDesc}</TableCell>
                    <TableCell className="whitespace-nowrap text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                      {BOOK_LABEL[r.facilityCode] ?? r.facilityCode}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{thr(r.thresholdPct)}</TableCell>
                    <TableCell className="text-muted-foreground">{r.categoryGroup}</TableCell>
                    <TableCell className="text-right">
                      {realPct !== null && realPct !== undefined ? `${realPct.toFixed(2)}%` : "—"}
                    </TableCell>
                    <TableCell className="text-right text-amber">{r.currentDollarPct?.toFixed(2)}%</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px] uppercase tracking-[0.1em]",
                          r.status === "GREEN" && "border-[color:var(--approve)]/50 text-[color:var(--approve)]",
                          r.status === "AMBER" && "border-amber/50 text-amber",
                          r.status === "RED" && "border-destructive/50 text-destructive",
                        )}
                      >
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{r.direction}</TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          )}

          {!loading &&
            result.notes.map((note, i) => (
              <p key={i} className="mt-2 text-[11px] text-muted-foreground">
                {note}
              </p>
            ))}
        </>
      ) : null}

      <div className="mt-4 flex justify-between">
        <Button variant="outline" onClick={() => navigate(`${base}/apply`)}>
          ← Scenario
        </Button>
        <Button disabled={!result} onClick={() => navigate(`${base}/snapshot`)}>
          View Scenario Snapshot →
        </Button>
      </div>
    </div>
  );
}
